import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { VehicleService } from 'app/services/vehicles.service';
import { IVehicle, VehiclesChartsData } from 'app/models/vehicle.models';
import { FilterService } from 'app/services/filter.service';
import { HeaderService } from 'app/services/header.service';
import { animateTopScroll } from 'app/utils/animation.utils';
import { IChartSelections, IGenerateDataFuncResult, IPieChartOptions, ISelectionsDTO } from 'app/models/chart.models';
import { Subscription } from 'rxjs';
import { PieChartComponent } from 'app/components/shared/pie-chart/pie-chart.component';
import { VehicleByMonthComponent } from './vehicle-by-month/vehicle-by-month.component';
import { IExportableChart } from 'app/utils/pdf.utils';
import { NEWER_OLDER_SELECTION } from 'app/constants/vehicle.contstant';
import { ISideDataGridColumns } from 'app/components/shared/side-grid/side-grid.component';
import { PageType } from 'app/models/pdf.models';
import { PdfService } from 'app/services/pdf.service';
import { CitationCharts } from 'app/models/citation-charts.models';
import { NgIf } from '@angular/common';
import { SideGridComponent } from '../../shared/side-grid/side-grid.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { CardComponent } from '../../shared/card/card.component';
import { PieChartComponent as PieChartComponent_1 } from '../../shared/pie-chart/pie-chart.component';
import { GridButtonComponent } from '../../shared/grid-button/grid-button.component';

@Component({
  selector: 'app-vehicles',
  templateUrl: './vehicles.component.html',
  styleUrls: ['./vehicles.component.scss'],
  imports: [
    NgIf,
    SideGridComponent,
    MatProgressSpinner,
    CardComponent,
    PieChartComponent_1,
    VehicleByMonthComponent,
    GridButtonComponent,
  ],
})
export class VehiclesComponent implements AfterViewInit, OnInit, OnDestroy {
  private readonly subscriptions: Subscription = new Subscription();

  vehicles: IVehicle[] = [];
  vehiclesData: VehiclesChartsData = {
    vehicleColors: [],
    vehicleTypes: [],
    vehicleMakes: [],
    citationsByVehicleAge: { name: '', values: [] },
  };
  isLoadingInitialData = false;
  isLoadingUpdate = false;
  isDataGridOpened = false;

  private exportSubscripton!: Subscription;

  @ViewChild('vehicleColorChart')
  vehicleColorChart!: PieChartComponent;

  @ViewChild('vehicleTypeChart')
  vehicleTypeChart!: PieChartComponent;

  @ViewChild('vehicleByMonthChart')
  vehicleByMonthChart!: VehicleByMonthComponent;

  // with accordance to docs and angular source code
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ViewChild('leftPanelTemplate') leftPanelTemplate!: TemplateRef<any>;

  readonly chartTitles = {
    VEHICLE_COLOR: 'Vehicle Color',
    VEHICLE_TYPE: 'Vehicle Type',
    NEWER_VS_OLDER: 'Citations - Newer vs Older',
  };

  constructor(
    private vehicleService: VehicleService,
    private filterService: FilterService,
    private headerService: HeaderService,
    private pdfService: PdfService,
  ) {}

  loadChartsData() {
    this.getVehiclesCharts();
  }

  ngOnInit(): void {
    this.isLoadingInitialData = true;
    this.loadChartsData();

    const filters = this.filterService.applyFiltersChange.subscribe(() => {
      this.clearSelections();
      this.loadChartsData();
    });
    this.subscriptions.add(filters);

    this.exportSubscripton = this.headerService.exportChange.subscribe(() => {
      this.exportToPdf();
    });
  }

  ngAfterViewInit(): void {
    this.headerService.leftPanel.next(this.leftPanelTemplate);
  }

  toggleDataGrid() {
    this.isDataGridOpened = !this.isDataGridOpened;
  }

  ngOnDestroy(): void {
    !!this.subscriptions && this.subscriptions.unsubscribe();
    this.exportSubscripton.unsubscribe();
    this.headerService.leftPanel.next(null);
  }

  combineSelectionsDTO(): ISelectionsDTO {
    const result = [];
    for (const chartName of Object.keys(this.defaultSelections)) {
      if (this.selections?.[chartName]?.length > 0) {
        result.push({
          chart: chartName,
          values: this.selections?.[chartName],
        });
      }
    }
    return result;
  }

  private getVehiclesCharts() {
    const { filters } = this.filterService;

    this.isLoadingUpdate = true;
    this.vehicleService.getCombinedVehiclesData(this.combineSelectionsDTO(), filters).subscribe((vehiclesData) => {
      this.vehiclesData = vehiclesData;
      this.isLoadingInitialData = false;
      this.isLoadingUpdate = false;
      this.selections = { ...this.selections };
    });
  }

  fullScreenChart: string | null = null;

  @ViewChild('scrollContainer') scrollContainer!: ElementRef<HTMLElement>;
  private scrollPosition = 0;

  vehicleTypeChartName = CitationCharts.VEHICLE_TYPE;
  vehicleColorChartName = CitationCharts.VEHICLE_COLOR;
  vehicleMakeGridSelectionName = CitationCharts.VEHICLE_MAKE;
  byMonthChartName = CitationCharts.MONTH;
  byMonthNewerOlderSelectionName = NEWER_OLDER_SELECTION;
  vehicleMakeGridColumns: ISideDataGridColumns[] = [
    { columnDef: 'label', header: 'Vehicle Make' },
    { columnDef: 'count', header: 'Count of Citation' },
  ];

  generateResultByColor = (): IGenerateDataFuncResult => {
    const total: number[] = [];
    const selected: number[] = [];
    const labels: string[] = [];
    for (const color of this.vehiclesData.vehicleColors) {
      const label = !color.label ? 'Unknown' : color.label;
      total.push(color.totalCount || 0);
      selected.push(color.count || 0);
      labels.push(label);
    }
    return {
      total: total,
      filtered: selected,
      labels: labels,
    };
  };
  resultByColorChartOptions: IPieChartOptions = {
    plugins: {
      legend: {
        title: {
          text: 'Color',
        },
      },
    },
  };

  generateResultByType = (): IGenerateDataFuncResult => {
    const total: number[] = [];
    const selected: number[] = [];
    const labels: string[] = [];
    for (const type of this.vehiclesData.vehicleTypes) {
      const label = !type.label ? 'Unknown' : type.label;
      total.push(type.totalCount || 0);
      selected.push(type.count || 0);
      labels.push(label);
    }
    return {
      total: total,
      filtered: selected,
      labels: labels,
    };
  };
  resultByTypeChartOptions: IPieChartOptions = {
    plugins: {
      legend: {
        title: {
          text: 'Type',
        },
      },
    },
  };

  toggleFullScreen(chart: string) {
    if (chart == this.fullScreenChart) {
      this.fullScreenChart = null;
      animateTopScroll(this.scrollPosition, this.scrollContainer.nativeElement);
    } else {
      this.fullScreenChart = chart;
      this.scrollPosition = this.scrollContainer.nativeElement.scrollTop;
    }
  }

  clearSelections(): void {
    this.selections = this.defaultSelections;
  }

  setSelections(selections: string[], resetOther: boolean, name: string) {
    const newSelections = resetOther ? { ...this.defaultSelections } : { ...this.selections };
    this.selections = { ...newSelections, [name]: selections };
    this.loadChartsData();
  }

  private defaultSelections = {
    [this.vehicleColorChartName]: [],
    [this.vehicleTypeChartName]: [],
    [this.vehicleMakeGridSelectionName]: [],
    [this.byMonthChartName]: [],
    [this.byMonthNewerOlderSelectionName]: [],
  };

  selections: IChartSelections = this.defaultSelections;

  exportToPdf() {
    let charts: IExportableChart[] = [
      {
        chart: this.vehicleColorChart.exportToImage(),
        name: this.chartTitles.VEHICLE_COLOR,
      },
      {
        chart: this.vehicleTypeChart.exportToImage(),
        name: this.chartTitles.VEHICLE_TYPE,
      },
      {
        chart: this.vehicleByMonthChart.exportToImage(),
        name: this.chartTitles.NEWER_VS_OLDER,
      },
    ];
    if (this.fullScreenChart) {
      charts = [charts[Number.parseInt(this.fullScreenChart)]];
    }
    this.pdfService.generateChartReportFile(
      PageType.VEHICLES,
      charts,
      this.filterService.filters,
      'tda_vehicles',
      'Report - Vehicles',
    );
  }
}
